// lib/greekNormalize.ts
// Greek phonetic normalization + basic stemming for improved search matching
// Converts phonetically equivalent characters to a single canonical form,
// then strips common Greek suffixes so plural/singular forms match.

/**
 * Normalizes Greek text by:
 * 1. Phonetic normalization — converting sound-alike chars to canonical form
 * 2. Basic stemming — stripping common Greek noun/adjective endings
 *
 * IMPORTANT: Digraphs where υ has a DIFFERENT sound must be handled before
 * the standalone υ→ι rule:
 *   ου/ού = "oo" sound (NOT "ee") — preserved as ου
 *   αυ/αύ = "av/af" sound — preserved as αυ
 *   ευ/εύ = "ev/ef" sound — preserved as ευ
 *
 * Examples:
 *   normalizeGreek("Ποτήρι")    → "ποτιρ"
 *   normalizeGreek("ποτίρι")    → "ποτιρ"     (typo now matches!)
 *   normalizeGreek("Ποτήρια")   → "ποτιρ"     (plural matches singular!)
 *   normalizeGreek("Μαχαίρι")   → "μαχερ"
 *   normalizeGreek("μαχέρι")    → "μαχερ"     (typo now matches!)
 *   normalizeGreek("σπάτουλα")  → "σπατουλ"
 *   normalizeGreek("σπάτουλες") → "σπατουλ"   (plural matches singular!)
 *   normalizeGreek("κουτάλι")   → "κουταλ"    (ου preserved!)
 *   normalizeGreek("αυγό")      → "αυγο"      (αυ preserved, too short to stem)
 *   normalizeGreek("τυρί")      → "τιρι"      (not stemmed — prevents false match with τιρμπουσόν)
 */
export function normalizeGreek(text: string | null | undefined): string {
  if (!text) return '';

  // Step 1: Phonetic normalization (per-word, then rejoin)
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);

  const processed = words.map(word => {
    // Phonetic normalization
    let w = word
      // "ee" sounds → ι (digraphs first, then single chars)
      .replace(/εί/g, 'ι')
      .replace(/ει/g, 'ι')
      .replace(/οί/g, 'ι')
      .replace(/οι/g, 'ι')
      .replace(/ή/g, 'ι')
      .replace(/η/g, 'ι')
      // Preserve υ-digraphs that are NOT "ee" sounds (must come BEFORE υ→ι)
      .replace(/ού/g, 'ου')
      .replace(/αύ/g, 'αυ')
      .replace(/εύ/g, 'ευ')
      // Standalone υ/ύ → ι (only when NOT part of ου, αυ, ευ digraphs)
      .replace(/(?<![οαε])ύ/g, 'ι')
      .replace(/(?<![οαε])υ/g, 'ι')
      // "eh" sound → ε
      .replace(/αί/g, 'ε')
      .replace(/αι/g, 'ε')
      // "oh" sound → ο
      .replace(/ώ/g, 'ο')
      .replace(/ω/g, 'ο')
      // Remove accents from remaining vowels
      .replace(/ά/g, 'α')
      .replace(/έ/g, 'ε')
      .replace(/ί/g, 'ι')
      .replace(/ό/g, 'ο');

    // Step 2: Basic Greek stemming (strip common endings)
    // Only stem words that contain Greek chars and are long enough
    if (w.length >= 4 && /[α-ω]/.test(w)) {
      w = stemGreekWord(w);
    }

    // Step 3: Homoglyph fold — unifies Greek and Latin alphabets for
    // technical-spec tokens like "16Α" ⇄ "16A", "4000Κ" ⇄ "4000K",
    // "Ε27" ⇄ "E27", "IΡ65" ⇄ "IP65". Restricted to tokens containing
    // digits so normal Greek vocabulary (διακόπτης, λάμπα, πλαστικό) is
    // never touched. The 10 chars below are Greek letters that look
    // visually identical (or near-identical) to a Latin letter — folding
    // them gives a single canonical form so both alphabets converge.
    if (/\d/.test(w)) {
      w = foldHomoglyphsInTechSpec(w);
    }

    return w;
  });

  return processed.join(' ');
}

/**
 * All matchable forms of a word/phrase, for Meilisearch SYNONYM KEYS.
 *
 * Search queries are normalized exactly ONCE before they reach Meilisearch, so
 * a synonym key fires iff a single normalizeGreek() pass of what the shopper
 * typed equals it. But normalizeGreek is not idempotent — different inflections
 * of the SAME word can land on different single-pass forms (καλώδιο → "καλοδι",
 * καλώδια → "καλοδ"). Iterating to the fixpoint and keeping every intermediate
 * form covers them all from one naturally-typed word:
 *
 *   normalizeChain("καλώδιο")   → ["καλοδι", "καλοδ"]
 *   normalizeChain("ασύρματος") → ["ασιρματ"]
 *   normalizeChain("air fryer") → ["air fryer"]
 *
 * Used by the dashboard synonyms editor to derive engine keys from the natural
 * word a merchant types (bounded loop — the string shrinks or stays put).
 */
export function normalizeChain(text: string | null | undefined): string[] {
  const chain: string[] = [];
  let current = normalizeGreek(text);
  for (let i = 0; current && !chain.includes(current) && i < 6; i++) {
    chain.push(current);
    current = normalizeGreek(current);
  }
  return chain;
}

// Greek → Latin homoglyphs. Lowercase only (normalizeGreek lowercases first).
// Conservative set: only chars where the Greek letter looks visually identical
// (or near-identical) to a Latin letter. Phonetic-but-different letters (β/v,
// γ/g, δ/d, λ/l, μ/m, ν/n, π/p, σ/s, φ/f, ψ/ps, ω/w) are NOT included — those
// are Greeklish's job (Latin→Greek, query-time only), and folding them here
// would collide with that flow.
const GREEK_TO_LATIN_HOMOGLYPHS: Record<string, string> = {
  'α': 'a', 'ε': 'e', 'η': 'h', 'ι': 'i', 'κ': 'k',
  'ο': 'o', 'ρ': 'p', 'τ': 't', 'υ': 'y', 'χ': 'x',
};

function foldHomoglyphsInTechSpec(word: string): string {
  let out = '';
  for (const ch of word) {
    out += GREEK_TO_LATIN_HOMOGLYPHS[ch] ?? ch;
  }
  return out;
}

/**
 * Basic Greek stemmer — strips common noun/adjective/verb endings.
 * Operates on already-normalized text (no accents, phonetic canonical form).
 * Minimum stem length: 4 characters (prevents over-stemming short words
 * that causes false prefix matches, e.g. τιρι→τιρ matching τιρμπουσόν).
 *
 * Endings are tried longest-first to avoid partial matches.
 */
function stemGreekWord(word: string): string {
  const MIN_STEM = 4;

  // Ordered longest → shortest to match greedily
  // These are the normalized forms (after phonetic normalization)
  const suffixes = [
    // 5 char derivational suffix (agent nouns: -αστής → -αστις after normalization)
    'αστις',            // agent noun: παρασκευαστής→παρασκευ (MIN_STEM protects short words like πλάστης)
    // 3+ char compound suffixes (adjective endings)
    'ικες',             // adjective fem plural: διακοσμητικές
    'ικον',             // adjective genitive: διακοσμητικών (after ω→ο)
    'ικα',              // adjective neuter plural: διακοσμητικά
    'ικο',              // adjective neuter singular: διακοσμητικό
    'ικι',              // adjective neuter (after η→ι): διακοσμητική
    'ους',              // masculine accusative plural: ανθρώπους
    'ιον',              // genitive plural: ποτηρίων (after ω→ο)
    // 2 char endings (most common Greek noun endings)
    'ες',               // feminine/neuter plural: σπάτουλες, μέρες
    'ις',               // genitive fem singular (after ή→ι): παρασκευής→παρασκευις
    'ια',               // neuter plural: ποτήρια, μαχαίρια
    'ος',               // masculine singular: άνθρωπος
    'ας',               // masculine singular: άντρας
    'ον',               // genitive plural (after ω→ο normalization)
    // 1 char endings
    'α',                // feminine singular: σπάτουλα, μέρα
    'ο',                // neuter singular: αυγό
    'ι',                // neuter singular: ποτήρι, μαχαίρι
    'ε',                // vocative: πατέρε
  ];

  for (const suffix of suffixes) {
    if (word.length - suffix.length >= MIN_STEM && word.endsWith(suffix)) {
      return word.slice(0, -suffix.length);
    }
  }

  return word;
}

/**
 * Check if a string contains Greek characters
 */
export function containsGreek(text: string): boolean {
  return /[\u0370-\u03FF\u1F00-\u1FFF]/.test(text);
}

/**
 * Check if normalization would change the text
 * Useful to avoid unnecessary processing
 */
export function wouldNormalizationChange(text: string): boolean {
  if (!text) return false;
  return normalizeGreek(text) !== text.toLowerCase();
}
